import { useState } from 'react';
import type { Block, Claim } from '../../../types/report';
import { GenericBlock } from './GenericBlock';
import { Claim as ClaimWrap } from '../Claim';
import { segmentColor } from './segmentColors';
import { mediaSrc } from '../mediaSrc';

/** Segment name cell with the segment's accent as a left color rule. */
function SegName({ name }: { name: string }) {
  return (
    <span className="seg-name" style={{ borderLeftColor: segmentColor(name) }}>
      {name}
    </span>
  );
}

export interface BlockProps {
  block: Block;
  claims: Record<string, Claim>;
}

function claimVal(claims: Record<string, Claim>, id?: string): string {
  return id && claims[id] ? claims[id].header.value : '';
}

/** stat-grid: headline stats, each a clickable claim (hero). */
function StatGridBlock({ block, claims }: BlockProps) {
  const ids = (block.data.claimIds as string[]) || [];
  return (
    <div className="hero-grid" data-block="stat-grid">
      {ids.map((id) => {
        const c = claims[id];
        if (!c) return null;
        return (
          <div key={id} className="hero-stat">
            <div className="hero-stat-label">{c.header.title}</div>
            <div className="hero-stat-value">
              <ClaimWrap id={id} type={c.type}>
                {c.header.value}
              </ClaimWrap>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** table: segments sized three ways (share / spend / % of dollars). */
function TableBlock({ block, claims }: BlockProps) {
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  const total = block.data.totalRow as Record<string, unknown> | undefined;
  const hasSpend = rows.some((r) => r.spendClaimId);
  const hasDollars = rows.some((r) => r.dollarClaimId);
  const hasMedian = rows.some((r) => r.medianClaimId);
  return (
    <table className="seg-table" data-block="table">
      <thead>
        <tr>
          <th>Segment</th>
          <th>% of buyers</th>
          {hasDollars && <th>% of spend</th>}
          {hasSpend && <th>Avg $/mo</th>}
          {hasMedian && <th>Median $/mo</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            <td>
              <SegName name={String(row.name ?? '')} />
            </td>
            <td className="seg-metric">
              <Cell claims={claims} id={row.shareClaimId as string} />
            </td>
            {hasDollars && (
              <td className="seg-metric">
                <Cell claims={claims} id={row.dollarClaimId as string} />
              </td>
            )}
            {hasSpend && (
              <td className="seg-metric">
                <Cell claims={claims} id={row.spendClaimId as string} />
              </td>
            )}
            {hasMedian && (
              <td className="seg-metric">
                <Cell claims={claims} id={row.medianClaimId as string} />
              </td>
            )}
          </tr>
        ))}
        {total && (
          <tr className="seg-total-row">
            <td>{String(total.name ?? 'All buyers')}</td>
            <td className="seg-metric">{String(total.share ?? '100%')}</td>
            {hasDollars && <td className="seg-metric">{String(total.dollars ?? '100%')}</td>}
            {hasSpend && (
              <td className="seg-metric">
                <Cell claims={claims} id={total.spendClaimId as string} />
              </td>
            )}
            {hasMedian && (
              <td className="seg-metric">
                <Cell claims={claims} id={total.medianClaimId as string} />
              </td>
            )}
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Cell({ claims, id }: { claims: Record<string, Claim>; id?: string }) {
  if (!id || !claims[id]) return <>{claimVal(claims, id)}</>;
  const c = claims[id];
  return (
    <ClaimWrap id={id} type={c.type}>
      {c.header.value}
    </ClaimWrap>
  );
}

/** matrix: two shapes —
 *  brand-index: data.rows = string[], data.cells = [{brand, segment, claimId}]
 *  triggers:    data.rows = [{label, cells: [{segment, claimId}]}]
 *  both render a row-label × cols grid of index/percent cells. */
function MatrixBlock({ block, claims }: BlockProps) {
  const cols = (block.data.cols as string[]) || [];
  const rawRows = (block.data.rows as unknown[]) || [];
  const flatCells = (block.data.cells as Array<Record<string, unknown>>) || [];
  const rowsAreObjects = rawRows.length > 0 && typeof rawRows[0] === 'object';

  const idxClass = (v: string) => {
    const n = parseInt(v, 10);
    if (isNaN(n)) return 'par';
    // percent cells (triggers) have no 100-baseline, so treat as neutral;
    // index cells (brand) use the over/under thresholds.
    if (v.includes('%')) return 'par';
    return n >= 120 ? 'over' : n <= 80 ? 'under' : 'par';
  };

  // normalize both shapes to { label, cellFor(col) -> claimId }
  const rows = rowsAreObjects
    ? (rawRows as Array<Record<string, unknown>>).map((r) => ({
        label: String(r.label ?? ''),
        cellFor: (col: string) =>
          ((r.cells as Array<Record<string, unknown>>) || []).find((c) => c.segment === col)
            ?.claimId as string | undefined,
      }))
    : (rawRows as string[]).map((label) => ({
        label,
        cellFor: (col: string) =>
          flatCells.find((c) => c.brand === label && c.segment === col)?.claimId as
            | string
            | undefined,
      }));

  return (
    <table className="biz-table" data-block="matrix">
      <thead>
        <tr>
          <th></th>
          {cols.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <td>{r.label}</td>
            {cols.map((col) => {
              const id = r.cellFor(col);
              const val = claimVal(claims, id);
              return (
                <td key={col}>
                  {id && claims[id] ? (
                    <ClaimWrap id={id} type={claims[id].type}>
                      <span className={`biz-index ${idxClass(val)}`}>{val}</span>
                    </ClaimWrap>
                  ) : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** bar-list: per-segment bracket/channel rows (demographics, channel). */
function BarListBlock({ block, claims }: BlockProps) {
  const segments = (block.data.segments as Array<Record<string, unknown>>) || [];
  // Labels are "dimension: bracket" (e.g. "gender: Woman"). Group by dimension
  // so demographics read as compact dimension blocks, segments side-by-side —
  // instead of one giant flat column.
  const splitLabel = (lbl: string): [string, string] => {
    const idx = lbl.indexOf(':');
    return idx > 0 ? [lbl.slice(0, idx).trim(), lbl.slice(idx + 1).trim()] : ['', lbl];
  };

  // collect dimensions and, per dimension, the ordered bracket labels
  const dims: string[] = [];
  const bracketsByDim: Record<string, string[]> = {};
  for (const seg of segments) {
    const items = ((seg.brackets || seg.channels) as Array<Record<string, unknown>>) || [];
    for (const it of items) {
      const [dim, bracket] = splitLabel(String(it.label ?? ''));
      const d = dim || 'value';
      if (!dims.includes(d)) {
        dims.push(d);
        bracketsByDim[d] = [];
      }
      if (!bracketsByDim[d].includes(bracket)) bracketsByDim[d].push(bracket);
    }
  }
  // Order ordinal brackets (age "18–24", income "$50–100K") by their leading
  // number so they read low→high; leaves non-numeric labels in first-seen order.
  const leadNum = (s: string) => {
    const m = s.replace(/,/g, '').match(/\d+/);
    return m ? Number(m[0]) : Number.POSITIVE_INFINITY;
  };
  for (const d of dims) {
    if (bracketsByDim[d].every((b) => /\d/.test(b))) {
      bracketsByDim[d].sort((a, b) => leadNum(a) - leadNum(b));
    }
  }
  // lookup: (segment index, dim, bracket) -> claimId
  const claimAt = (seg: Record<string, unknown>, dim: string, bracket: string) => {
    const items = ((seg.brackets || seg.channels) as Array<Record<string, unknown>>) || [];
    const found = items.find((it) => {
      const [d, b] = splitLabel(String(it.label ?? ''));
      return (d || 'value') === dim && b === bracket;
    });
    return found?.claimId as string | undefined;
  };

  return (
    <div data-block="bar-list">
      {dims.map((dim) => (
        <div key={dim} className="demo-dim">
          {dim !== 'value' && <div className="demo-dim-name">{dim}</div>}
          <table className="biz-table">
            <thead>
              <tr>
                <th></th>
                {segments.map((s, i) => (
                  <th key={i}>{String(s.segment ?? '')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bracketsByDim[dim].map((bracket) => (
                <tr key={bracket}>
                  <td>{bracket}</td>
                  {segments.map((s, i) => {
                    const id = claimAt(s, dim, bracket);
                    const c = id ? claims[id] : undefined;
                    return (
                      <td key={i} className="seg-metric">
                        {c ? (
                          <ClaimWrap id={id!} type={c.type}>
                            {c.header.value}
                          </ClaimWrap>
                        ) : (
                          '—'
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/** maxdiff: per-segment claim utilities. */
function MaxDiffBlock({ block, claims }: BlockProps) {
  const segments = (block.data.segments as Array<Record<string, unknown>>) || [];
  return (
    <div data-block="maxdiff">
      {segments.map((seg, i) => (
        <div key={i} className="md-seg">
          <div
            className="bar-seg-name"
            style={{ borderLeftColor: segmentColor(String(seg.segment ?? '')) }}
          >
            {String(seg.segment ?? '')}
          </div>
          {((seg.claims as Array<Record<string, unknown>>) || []).map((row, j) => {
            const id = row.claimId as string;
            const c = claims[id];
            return (
              <div key={j} className="md-row">
                <span>{String(row.label ?? '')}</span>
                <span className="md-util">
                  {c ? (
                    <ClaimWrap id={id} type={c.type}>
                      {c.header.value}
                    </ClaimWrap>
                  ) : (
                    ''
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** split-claims: two cards (Defectors vs Core/still-loyal) showing which claims
 *  move each group — the mock's "claims that split the franchise". */
function SplitClaimsBlock({ block, claims }: BlockProps) {
  const cards = (block.data.cards as Array<Record<string, unknown>>) || [];
  const desc = block.data.desc as string | undefined;
  return (
    <div data-block="split-claims">
      {desc && <p className="split-claims-intro">{desc}</p>}
      <div className="split-claims-grid">
        {cards.map((card, i) => (
          <div key={i} className={`split-claims-card ${String(card.variant ?? '')}`}>
            <div className="split-claims-label">{String(card.label ?? '')}</div>
            {card.desc ? <div className="split-claims-desc">{String(card.desc)}</div> : null}
            {((card.claims as Array<Record<string, unknown>>) || []).map((row, j) => {
              const id = row.claimId as string | undefined;
              const c = id ? claims[id] : undefined;
              const util = <span className="split-claim-util">{String(row.util ?? '')}</span>;
              return (
                <div key={j} className="split-claim-row">
                  <span className="split-claim-text">{String(row.label ?? '')}</span>
                  {c ? (
                    <ClaimWrap id={id!} type={c.type}>
                      {util}
                    </ClaimWrap>
                  ) : (
                    util
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** chip-row: archetype chips (Type-D entities). */
function ChipRowBlock({ block, claims }: BlockProps) {
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  return (
    <div className="arch-row" data-block="chip-row">
      {rows.map((row, i) => {
        const id = row.entityClaimId as string;
        const c = claims[id];
        return (
          <div key={i} className="arch-chip">
            {String(row.segment ?? '')}:{' '}
            <b>
              {c ? (
                <ClaimWrap id={id} type={c.type}>
                  {String(row.archetype ?? c.header.value)}
                </ClaimWrap>
              ) : (
                String(row.archetype ?? '')
              )}
            </b>
          </div>
        );
      })}
    </div>
  );
}

/** prose: narrative paragraphs from prose claims (Type B/D). */
function ProseBlock({ block, claims }: BlockProps) {
  const ids = (block.data.claimIds as string[]) || [];
  return (
    <div data-block="prose">
      {ids.map((id) => {
        const c = claims[id];
        if (!c) return null;
        return (
          <div key={id} className="prose-claim">
            {c.lede && <p className="prose-lede">{c.lede}</p>}
            {(c.commentary || []).map((p, j) => (
              <p key={j} className="prose-para">
                {p}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** scatter: two-axis segment map (placements 0..1 on x/y). Bubble diameter is
 *  scaled by each segment's `size` (share of buyers) so the map encodes
 *  headcount — the biggest segment reads as the biggest circle. Each bubble is
 *  clickable when it carries a `claimId` (drills into that segment). */
function ScatterBlock({ block, claims }: BlockProps) {
  const xAxis = String(block.data.xAxis ?? '');
  const yAxis = String(block.data.yAxis ?? '');
  const sizeLegend = block.data.sizeLegend as string | undefined;
  const points = (block.data.placements as Array<Record<string, unknown>>) || [];
  const sizes = points
    .map((p) => (typeof p.size === 'number' ? (p.size as number) : NaN))
    .filter((n) => !isNaN(n));
  const maxSize = sizes.length ? Math.max(...sizes) : 0;
  const MIN_D = 40,
    MAX_D = 104;
  const diameter = (p: Record<string, unknown>) => {
    if (typeof p.size !== 'number' || !maxSize) return 12;
    return MIN_D + (MAX_D - MIN_D) * Math.sqrt((p.size as number) / maxSize);
  };
  // split an "Axis (low ↔ high)" label into name + two endpoint labels
  const ends = (label: string): [string, string, string] => {
    const m = label.match(/^(.*?)\s*\(([^↔]+)↔([^)]+)\)\s*$/);
    if (m) return [m[1].trim(), m[2].trim(), m[3].trim()];
    return [label, '', ''];
  };
  const [xName, xLow, xHigh] = ends(xAxis);
  const [yName, yLow, yHigh] = ends(yAxis);
  return (
    <div className="two-axis" data-block="scatter">
      <div className="two-axis-help">
        How to read it · {xAxis}; {yAxis}. Bubble size ∝ share of buyers. Click any bubble for that
        segment.
      </div>
      <div className="two-axis-frame">
        {yHigh && <span className="axis-end axis-end-top">{yHigh}</span>}
        <div className="two-axis-plot">
          {points.map((p, i) => {
            const d = diameter(p);
            const cid = p.claimId as string | undefined;
            const share =
              typeof p.size === 'number' ? `${Math.round(p.size as number)}% of buyers` : '';
            const dot = (
              <span
                className="axis-dot"
                style={{
                  background: segmentColor(String(p.segment ?? '')),
                  width: `${d}px`,
                  height: `${d}px`,
                  opacity: typeof p.size === 'number' ? 0.85 : 1,
                }}
              />
            );
            return (
              <div
                key={i}
                className="axis-point"
                style={{ left: `${Number(p.x) * 100}%`, bottom: `${Number(p.y) * 100}%` }}
                title={String(p.note ?? '')}
              >
                {cid && claims[cid] ? (
                  <ClaimWrap id={cid} type={claims[cid].type}>
                    {dot}
                  </ClaimWrap>
                ) : (
                  dot
                )}
                <span className="axis-label">{String(p.segment ?? '')}</span>
                {share && <span className="axis-share">{share}</span>}
              </div>
            );
          })}
          {yLow && <span className="axis-end axis-end-bottom">{yLow}</span>}
          {xLow && <span className="axis-end axis-end-left">{xLow}</span>}
          {xHigh && <span className="axis-end axis-end-right">{xHigh}</span>}
        </div>
        <span className="axis-name axis-name-y">{yName || yAxis}</span>
      </div>
      <div className="axis-name axis-name-x">{xName || xAxis}</div>
      {sizeLegend && <div className="two-axis-legend">{sizeLegend}</div>}
    </div>
  );
}

/** vector-grid: segment × growth-vector strength grid. */
function VectorGridBlock({ block }: BlockProps) {
  const vectors = (block.data.vectors as string[]) || [];
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  const strengthFor = (row: Record<string, unknown>, v: string) =>
    ((row.cells as Array<Record<string, unknown>>) || []).find((c) => c.vector === v)?.strength as
      | string
      | undefined;
  return (
    <table className="biz-table" data-block="vector-grid">
      <thead>
        <tr>
          <th></th>
          {vectors.map((v) => (
            <th key={v}>{v}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>
              <SegName name={String(r.segment ?? '')} />
            </td>
            {vectors.map((v) => {
              const s = strengthFor(r, v) || 'none';
              return (
                <td key={v}>
                  <span className={`vector-cell ${s}`}>{s}</span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** focus-brand: per-segment fed vs considered (vs rejected) for one brand. */
function FocusBrandBlock({ block, claims }: BlockProps) {
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  const hasRejected = rows.some((r) => r.rejectedClaimId);
  const slIds = (block.data.stayerLeaverClaimIds as string[]) || [];
  return (
    <>
      {slIds.length > 0 && (
        <div className="hero-grid" style={{ marginBottom: 20 }} data-block="stayer-leaver">
          {slIds.map((id) => {
            const c = claims[id];
            if (!c) return null;
            return (
              <div key={id} className="hero-stat">
                <div className="hero-stat-label">{c.header.title}</div>
                <div className="hero-stat-value">
                  <ClaimWrap id={id} type={c.type}>
                    {c.header.value}
                  </ClaimWrap>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <table className="seg-table" data-block="focus-brand">
        <thead>
          <tr>
            <th>Segment</th>
            <th>Feeds it</th>
            <th>Considers it</th>
            {hasRejected && <th>Rejects it</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <SegName name={String(r.segment ?? '')} />
              </td>
              <td className="seg-metric">
                <Cell claims={claims} id={r.fedClaimId as string} />
              </td>
              <td className="seg-metric">
                <Cell claims={claims} id={r.consideredClaimId as string} />
              </td>
              {hasRejected && (
                <td className="seg-metric">
                  <Cell claims={claims} id={r.rejectedClaimId as string} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** verbatim: a real respondent quote (Type-E), gold-left-bar italic pull-quote
 *  with a mono attribution line (mock .segment-quote / .detail-verbatim). */
function VerbatimBlock({ block, claims }: BlockProps) {
  const id = block.data.claimId as string | undefined;
  const c = id ? claims[id] : undefined;
  const text = (c?.verbatim?.text as string) || (block.data.text as string) || '';
  const attr = (c?.verbatim?.attribution as string) || (block.data.attribution as string) || '';
  if (!text) return <></>;
  const body = (
    <div className="segment-quote" data-block="verbatim">
      <span className="segment-quote-mark">“</span>
      {text}
      {attr && <span className="segment-quote-attr">{attr}</span>}
    </div>
  );
  return c ? (
    <ClaimWrap id={id!} type={c.type}>
      {body}
    </ClaimWrap>
  ) : (
    body
  );
}

/** segment-profile: the per-segment deep-dive, organized into tabs
 *  (Overview / In Detail / Profile / Decision Engine — mock's segment tabs).
 *  Tabs render only when their content exists, so it degrades gracefully. */
function SegmentProfileBlock({ block, claims }: BlockProps) {
  const name = String(block.data.segment ?? '');
  const statIds = (block.data.statClaimIds as string[]) || [];
  const verbatimId = block.data.verbatimClaimId as string | undefined;
  const narrId = block.data.narrativeClaimId as string | undefined;
  const dilId = block.data.dayInLifeClaimId as string | undefined;
  const typingRows = (block.data.typingRows as Array<Record<string, unknown>>) || [];
  const mechanics = (block.data.mechanics as Array<Record<string, unknown>>) || [];
  const snapshot = (block.data.snapshot as Array<Record<string, unknown>>) || [];
  const brandBars = (block.data.brandBars as Record<string, Array<Record<string, unknown>>>) || {};
  const pantry = block.data.pantry as
    | { observedNClaimId?: string; tiles?: Array<Record<string, unknown>> }
    | undefined;
  const verb = verbatimId ? claims[verbatimId] : undefined;
  const narr = narrId ? claims[narrId] : undefined;
  const dil = dilId ? claims[dilId] : undefined;
  const dilText = dil?.commentary && dil.commentary.length > 0 ? dil.commentary[0] : '';
  const accent = segmentColor(name);

  // ── tab content renderers ──
  const overview = (
    <>
      {verb?.verbatim?.text && (
        <VerbatimBlock
          block={{ type: 'verbatim', data: { claimId: verbatimId } }}
          claims={claims}
        />
      )}
      {narr && (narr.lede || (narr.commentary && narr.commentary.length > 0)) && (
        <div className="segment-profile-narr">
          {narr.lede && <p className="segment-profile-lede">{narr.lede}</p>}
          {(narr.commentary || []).map((p, j) => (
            <p key={j}>{p}</p>
          ))}
        </div>
      )}
      {statIds.length > 0 && (
        <div className="segment-profile-stats" style={{ borderTopColor: accent }}>
          {statIds.map((id) => {
            const c = claims[id];
            if (!c) return null;
            return (
              <div key={id} className="segment-profile-stat">
                <div className="segment-profile-stat-value">
                  <ClaimWrap id={id} type={c.type}>
                    {c.header.value}
                  </ClaimWrap>
                </div>
                <div className="segment-profile-stat-label">
                  {c.header.title?.replace(`${name} — `, '')}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
  const detail = (
    <>
      {typingRows.length > 0 && (
        <div className="typing-tool" style={{ borderLeftColor: accent }}>
          <div className="typing-tool-label">How you'll know it's them</div>
          {typingRows.map((r, i) => (
            <div key={i} className="typing-q">
              <div className="typing-q-prompt">{String(r.q ?? '')}</div>
              <div className="typing-q-answer">{String(r.a ?? '')}</div>
            </div>
          ))}
        </div>
      )}
      {dilText && (
        <div className="day-in-life">
          <div className="day-in-life-label">A day in the life</div>
          {dil ? (
            <ClaimWrap id={dilId!} type={dil.type}>
              <div className="day-in-life-body">{dilText}</div>
            </ClaimWrap>
          ) : (
            <div className="day-in-life-body">{dilText}</div>
          )}
        </div>
      )}
    </>
  );
  const pantryTiles = pantry?.tiles ?? [];
  const observedN = pantry?.observedNClaimId ? claims[pantry.observedNClaimId] : undefined;
  const pantryBlock =
    pantryTiles.length > 0 ? (
      <div className="pantry-block">
        <div className="pantry-label">
          <span>◈ Pantry &amp; fridge audit</span>
          {observedN && (
            <span className="pantry-n">
              Observed n=
              <ClaimWrap id={pantry!.observedNClaimId!} type={observedN.type}>
                {observedN.header.value}
              </ClaimWrap>{' '}
              · click any tile
            </span>
          )}
        </div>
        <div className="pantry-grid">
          {pantryTiles.map((t, i) => {
            const cid = t.claimId as string | undefined;
            const products = (t.products as string[]) || [];
            const img = (
              <div className="pantry-tile">
                <img
                  src={mediaSrc(String(t.url ?? ''))}
                  alt={products.join(', ') || 'Pantry photo'}
                  loading="lazy"
                />
                <div className="pantry-tile-tag">Customer {String(t.customerId ?? '')}</div>
              </div>
            );
            return cid && claims[cid] ? (
              <ClaimWrap key={i} id={cid} type={claims[cid].type}>
                {img}
              </ClaimWrap>
            ) : (
              <span key={i}>{img}</span>
            );
          })}
        </div>
      </div>
    ) : null;

  const profile =
    snapshot.length > 0 || pantryBlock ? (
      <>
        {snapshot.length > 0 && (
          <div className="profile-snapshot">
            {snapshot.map((r, i) => (
              <div key={i} className="profile-snap-item">
                <div className="profile-snap-label">{String(r.label ?? '')}</div>
                <div className="profile-snap-value">{String(r.value ?? '')}</div>
              </div>
            ))}
          </div>
        )}
        {pantryBlock}
      </>
    ) : null;
  const brandCol = (heading: string, bars: Array<Record<string, unknown>>) => (
    <div className="brand-list-col">
      <div className="brand-list-head">{heading}</div>
      {bars.map((b, i) => (
        <div key={i} className="brand-row">
          <span className="brand-row-name">{String(b.label ?? '')}</span>
          <span className="brand-row-track">
            <span
              className="brand-row-fill"
              style={{ width: `${typeof b.width === 'number' ? b.width : 0}%` }}
            />
          </span>
          <span className="brand-row-pct">{String(b.value ?? '')}</span>
        </div>
      ))}
    </div>
  );
  const hasBrandBars = brandBars.trust?.length || brandBars.reject?.length;
  const decision =
    mechanics.length > 0 || hasBrandBars ? (
      <>
        {mechanics.length > 0 && (
          <div className="detail-mechanics">
            {mechanics.map((m, i) => (
              <div key={i} className="detail-mech-row">
                <div className="detail-mech-label">{String(m.label ?? '')}</div>
                <div className="detail-mech-body">{String(m.body ?? '')}</div>
              </div>
            ))}
          </div>
        )}
        {hasBrandBars && (
          <div className="brand-list-grid">
            {brandBars.trust?.length ? brandCol('Trusts · % of segment', brandBars.trust) : null}
            {brandBars.reject?.length ? brandCol('Rejects · % of segment', brandBars.reject) : null}
          </div>
        )}
      </>
    ) : null;

  const tabs: Array<{ key: string; label: string; content: React.ReactNode }> = [
    { key: 'overview', label: 'Overview', content: overview },
    {
      key: 'detail',
      label: 'In Detail',
      content: typingRows.length > 0 || dilText ? detail : null,
    },
    { key: 'profile', label: 'Profile', content: profile },
    { key: 'decision', label: 'Decision Engine', content: decision },
  ].filter((t) => t.content);

  const [active, setActive] = useState(tabs[0]?.key ?? 'overview');
  const activeContent = tabs.find((t) => t.key === active)?.content ?? tabs[0]?.content;

  const entity = (narr?.entity ?? {}) as Record<string, unknown>;
  const nickname = entity.nickname ? String(entity.nickname) : '';
  const tagline = entity.tagline ? String(entity.tagline) : '';

  return (
    <div className="segment-profile" data-block="segment-profile">
      {nickname && <div className="segment-persona">{nickname}</div>}
      {tagline && <div className="segment-tagline">“{tagline}”</div>}
      {tabs.length > 1 && (
        <div className="segment-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active === t.key}
              className={`segment-tab${active === t.key ? ' active' : ''}`}
              style={active === t.key ? { color: accent } : undefined}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className="segment-tab-content">{activeContent}</div>
    </div>
  );
}

/** closing-frame: dark full-bleed synthesis band — title + labeled blocks + footer. */
function ClosingFrameBlock({ block }: BlockProps) {
  const blocks = (block.data.blocks as Array<Record<string, unknown>>) || [];
  return (
    <div className="closing-frame" data-block="closing-frame">
      <div className="closing-eyebrow">Closing frame</div>
      <div className="closing-title">{String(block.data.title ?? '')}</div>
      <div className="closing-blocks">
        {blocks.map((b, i) => (
          <div key={i} className="closing-block">
            <div className="closing-block-title">{String(b.label ?? '')}</div>
            <div className="closing-block-body">{String(b.body ?? '')}</div>
          </div>
        ))}
      </div>
      {block.data.footer ? <div className="closing-footer">{String(block.data.footer)}</div> : null}
    </div>
  );
}

/** act-divider: full-bleed dark chapter band between report acts. */
function ActDividerBlock({ block }: BlockProps) {
  return (
    <div className="act-divider" data-block="act-divider">
      {block.data.eyebrow ? (
        <div className="act-divider-eyebrow">{String(block.data.eyebrow)}</div>
      ) : null}
      <div className="act-divider-title">{String(block.data.title ?? '')}</div>
      {block.data.desc ? <div className="act-divider-desc">{String(block.data.desc)}</div> : null}
    </div>
  );
}

/** marquee: dark gradient "big opportunity" treatment reusing a prose claim —
 *  lede becomes the marquee lede, paragraphs become play cards. */
function MarqueeBlock({ block, claims }: BlockProps) {
  const id = block.data.claimId as string | undefined;
  const c = id ? claims[id] : undefined;
  const eyebrow = String(block.data.eyebrow ?? '');
  const plays = (c?.commentary ?? []) as string[];
  const statIds = (block.data.statClaimIds as string[]) || [];
  const stats = statIds.map((sid) => claims[sid]).filter(Boolean) as Claim[];
  return (
    <div className="marquee" data-block="marquee">
      {eyebrow && <div className="marquee-eyebrow">{eyebrow}</div>}
      <div className="marquee-title">{c?.header.title ?? ''}</div>
      {c?.lede && <div className="marquee-lede">{c.lede}</div>}
      {stats.length > 0 && (
        <div className="marquee-stats">
          {stats.map((sc) => (
            <div key={sc.id} className="marquee-stat">
              <div className="marquee-stat-value">{sc.header.value}</div>
              <div className="marquee-stat-label">{sc.header.title}</div>
            </div>
          ))}
        </div>
      )}
      {plays.length > 0 && (
        <div className="marquee-plays">
          {plays.map((p, i) => (
            <div key={i} className="marquee-play">
              <div className="marquee-play-body">{p}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** cover: full-bleed dark title slide (mock #cover). */
function CoverBlock({ block, claims }: BlockProps) {
  const stats = (block.data.stats as Array<Record<string, unknown>>) || [];
  return (
    <div className="report-cover" data-block="cover">
      {block.data.brand ? <div className="cover-brand">{String(block.data.brand)}</div> : null}
      {block.data.eyebrow ? (
        <div className="cover-eyebrow">{String(block.data.eyebrow)}</div>
      ) : null}
      <div className="cover-title">{String(block.data.title ?? '')}</div>
      {block.data.lede ? <div className="cover-lede">{String(block.data.lede)}</div> : null}
      {stats.length > 0 && (
        <div className="cover-stats">
          {stats.map((s, i) => {
            const cid = s.claimId as string | undefined;
            const c = cid ? claims[cid] : undefined;
            const value = c ? c.header.value : String(s.value ?? '');
            const valEl = c ? (
              <ClaimWrap id={cid!} type={c.type}>
                {value}
              </ClaimWrap>
            ) : (
              value
            );
            return (
              <div key={i} className="cover-stat">
                <div className="cover-stat-value">{valEl}</div>
                <div className="cover-stat-label">{String(s.label ?? '')}</div>
              </div>
            );
          })}
        </div>
      )}
      {block.data.footer ? <div className="cover-footer">{String(block.data.footer)}</div> : null}
    </div>
  );
}

/** question-cards: 3 numbered cards (mock .three-q-grid). */
function QuestionCardsBlock({ block }: BlockProps) {
  const cards = (block.data.cards as Array<Record<string, unknown>>) || [];
  const note = (block.data.confidenceNote as Array<Record<string, unknown>>) || [];
  const tierColor: Record<string, string> = {
    Measured: '#065F46',
    Modeled: '#0F766E',
    Directional: '#8B3A3A',
  };
  return (
    <div data-block="question-cards">
      <div className="three-q-grid">
        {cards.map((c, i) => (
          <div key={i} className={`three-q-card q${(i % 3) + 1}`}>
            <div className="three-q-num">{String(c.n ?? i + 1)}</div>
            <div className="three-q-title">{String(c.question ?? '')}</div>
            <div className="three-q-body">{String(c.body ?? '')}</div>
          </div>
        ))}
      </div>
      {note.length > 0 && (
        <div className="confidence-note">
          <strong>A note on confidence tiers.</strong>{' '}
          {note.map((n, i) => (
            <span key={i}>
              <span style={{ color: tierColor[String(n.tier)] ?? 'inherit', fontWeight: 600 }}>
                {String(n.tier)}
              </span>{' '}
              — {String(n.text)}
              {'  '}
            </span>
          ))}
          Read aggressively against Measured, carefully against Modeled, and Directional as framing.
        </div>
      )}
    </div>
  );
}

/** exec-summary: lede + clickable per-segment rows + headline facts box. */
function ExecSummaryBlock({ block, claims }: BlockProps) {
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  const facts = (block.data.headlineFacts as string[]) || [];
  return (
    <div data-block="exec-summary">
      {block.data.lede ? <p className="exec-lede">{String(block.data.lede)}</p> : null}
      {/* two-column: segment list (left) + headline facts (right), like the mock */}
      <div className="exec-grid">
        <div className="exec-segments-list">
          {rows.map((r, i) => {
            const name = String(r.segment ?? '');
            const shareId = r.shareClaimId as string | undefined;
            const dollarId = r.dollarClaimId as string | undefined;
            const share = shareId && claims[shareId] ? claims[shareId].header.value : '';
            const dollar = dollarId && claims[dollarId] ? claims[dollarId].header.value : '';
            return (
              <div key={i} className="exec-segment-row">
                <span className="exec-segment-color" style={{ background: segmentColor(name) }} />
                <div className="exec-segment-main">
                  <div className="exec-segment-name">{name}</div>
                  {r.descriptor ? (
                    <div className="exec-segment-tag">{String(r.descriptor)}</div>
                  ) : null}
                </div>
                <div className="exec-segment-stats">
                  {shareId && claims[shareId] ? (
                    <ClaimWrap id={shareId} type={claims[shareId].type}>
                      {share}
                    </ClaimWrap>
                  ) : (
                    <span>{share}</span>
                  )}
                  {dollar ? <span className="exec-segment-stat-sub">{dollar} of $</span> : null}
                </div>
              </div>
            );
          })}
        </div>
        {facts.length > 0 && (
          <div className="headline-facts">
            <div className="headline-facts-label">The headline facts</div>
            {facts.map((f, i) => (
              <div key={i} className="fact-item">
                {f}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** insight-cards: lede + a card grid (+ optional signal callout). Turns a
 *  text-heavy narrative section into the mock's card-grid treatment. */
function InsightCardsBlock({ block }: BlockProps) {
  const cards = (block.data.cards as Array<Record<string, unknown>>) || [];
  const takeaway = block.data.takeaway ? String(block.data.takeaway) : '';
  // 4 cards read better 2-up; 3 (or other) use the 3-up grid.
  const gridClass = cards.length % 2 === 0 ? 'insight-grid cols-2' : 'insight-grid';
  return (
    <div data-block="insight-cards">
      {block.data.lede ? <p className="insight-lede">{String(block.data.lede)}</p> : null}
      <div className={gridClass}>
        {cards.map((c, i) => (
          <div key={i} className="insight-card">
            <div className="insight-card-title">{String(c.title ?? '')}</div>
            <div className="insight-card-body">{String(c.body ?? '')}</div>
          </div>
        ))}
      </div>
      {takeaway && <div className="signal-callout">{takeaway}</div>}
    </div>
  );
}

/** opp-table: per-segment strategy framework (priority · play · wedge · don't-say). */
function OppTableBlock({ block }: BlockProps) {
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  const prClass = (p: string) => {
    const s = p.toLowerCase();
    if (s.startsWith('prim')) return 'primary';
    if (s.startsWith('sec')) return 'secondary';
    if (s.startsWith('ret')) return 'retain';
    return 'low';
  };
  return (
    <table className="opp-table" data-block="opp-table">
      <thead>
        <tr>
          <th>Segment</th>
          <th>Priority</th>
          <th>Play</th>
          <th>Wedge</th>
          <th>Don't say</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="opp-seg">
              <span
                className="exec-segment-color"
                style={{ background: segmentColor(String(r.segment ?? '')) }}
              />
              {String(r.segment ?? '')}
            </td>
            <td>
              <span className={`opp-priority ${prClass(String(r.priority ?? ''))}`}>
                {String(r.priority ?? '')}
              </span>
            </td>
            <td>{String(r.play ?? '')}</td>
            <td>{String(r.wedge ?? '')}</td>
            <td className="opp-dont">{String(r.dontSay ?? '')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** ranked-bars: horizontal bar chart, one row per segment, bars scaled to the
 *  max, each value clickable to its claim (mock's .openness-row). */
function RankedBarsBlock({ block, claims }: BlockProps) {
  const rows = (block.data.rows as Array<Record<string, unknown>>) || [];
  const grad = (block.data.graduation as Array<Record<string, unknown>>) || [];
  return (
    <div data-block="ranked-bars">
      {block.data.lede ? <p className="insight-lede">{String(block.data.lede)}</p> : null}
      {block.data.caption ? (
        <div className="ranked-caption">{String(block.data.caption)}</div>
      ) : null}
      <div className="ranked-bars">
        {rows.map((r, i) => {
          const name = String(r.segment ?? '');
          const cid = r.claimId as string | undefined;
          const c = cid ? claims[cid] : undefined;
          const value = c ? c.header.value : String(r.value ?? '');
          const width = typeof r.width === 'number' ? r.width : 0;
          return (
            <div key={i} className="ranked-row">
              <span className="ranked-name" style={{ borderLeftColor: segmentColor(name) }}>
                {name}
              </span>
              <span className="ranked-track">
                <span
                  className="ranked-fill"
                  style={{ width: `${width}%`, background: segmentColor(name) }}
                />
              </span>
              <span className="ranked-pct">
                {c ? (
                  <ClaimWrap id={cid!} type={c.type}>
                    {value}
                  </ClaimWrap>
                ) : (
                  value
                )}
              </span>
            </div>
          );
        })}
      </div>
      {grad.length > 0 && (
        <div className="graduation-map">
          <div className="graduation-label">Graduation map · segment-to-segment moves</div>
          {grad.map((g, i) => (
            <div key={i} className="grad-row">
              <span
                className="grad-name"
                style={{ borderLeftColor: segmentColor(String(g.from ?? '')) }}
              >
                {String(g.from ?? '')}
              </span>
              <span className="grad-arrow">→ {String(g.trigger ?? '')}</span>
              <span
                className="grad-name"
                style={{ borderLeftColor: segmentColor(String(g.to ?? '')) }}
              >
                {String(g.to ?? '')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** stat-callout: a lede + big-number stat-fact tiles (mock .stat-fact). Each
 *  tile is claim-backed (clickable) when it has a claimId, else a plain value. */
function StatCalloutBlock({ block, claims }: BlockProps) {
  const stats = (block.data.stats as Array<Record<string, unknown>>) || [];
  const recs = (block.data.recs as Array<Record<string, unknown>>) || [];
  return (
    <div data-block="stat-callout">
      {block.data.lede ? <p className="insight-lede">{String(block.data.lede)}</p> : null}
      <div className="stat-fact-grid">
        {stats.map((s, i) => {
          const cid = s.claimId as string | undefined;
          const c = cid ? claims[cid] : undefined;
          const value = c ? c.header.value : String(s.value ?? '');
          return (
            <div key={i} className="stat-fact">
              <div className="stat-fact-num">
                {c ? (
                  <ClaimWrap id={cid!} type={c.type}>
                    {value}
                  </ClaimWrap>
                ) : (
                  value
                )}
              </div>
              <div className="stat-fact-title">{String(s.label ?? '')}</div>
              {s.body ? <div className="stat-fact-body">{String(s.body)}</div> : null}
            </div>
          );
        })}
      </div>
      {recs.length > 0 && (
        <div className="rec-grid">
          {recs.map((r, i) => (
            <div key={i} className="rec-card">
              <div className="rec-card-title">{String(r.label ?? '')}</div>
              <div className="rec-card-body">{String(r.body ?? '')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** compare-cards: two contrasting cards (e.g. stayers vs leavers), each with a
 *  claim-backed count, kicker, title, body (mock .sl-card). */
function CompareCardsBlock({ block, claims }: BlockProps) {
  const cards = (block.data.cards as Array<Record<string, unknown>>) || [];
  const takeaway = block.data.takeaway ? String(block.data.takeaway) : '';
  const recs = (block.data.recommendations as string[]) || [];
  return (
    <div data-block="compare-cards">
      {block.data.lede ? <p className="insight-lede">{String(block.data.lede)}</p> : null}
      <div className="compare-grid">
        {cards.map((c, i) => {
          const cid = c.statClaimId as string | undefined;
          const claim = cid ? claims[cid] : undefined;
          return (
            <div key={i} className={`compare-card ${String(c.tone ?? '')}`}>
              <div className="compare-head">
                <span className="compare-kicker">{String(c.kicker ?? '')}</span>
                {claim ? (
                  <span className="compare-stat">
                    <ClaimWrap id={cid!} type={claim.type}>
                      {claim.header.value}
                    </ClaimWrap>
                  </span>
                ) : null}
              </div>
              <div className="compare-title">{String(c.title ?? '')}</div>
              <div className="compare-body">{String(c.body ?? '')}</div>
            </div>
          );
        })}
      </div>
      {takeaway && <div className="signal-callout">{takeaway}</div>}
      {recs.length > 0 && (
        <ol className="rec-list">
          {recs.map((r, i) => (
            <li key={i} className="rec-list-item">
              {r}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const REGISTRY: Record<string, (props: BlockProps) => JSX.Element> = {
  'stat-grid': StatGridBlock,
  table: TableBlock,
  matrix: MatrixBlock,
  'bar-list': BarListBlock,
  maxdiff: MaxDiffBlock,
  'split-claims': SplitClaimsBlock,
  'chip-row': ChipRowBlock,
  prose: ProseBlock,
  scatter: ScatterBlock,
  'vector-grid': VectorGridBlock,
  'focus-brand': FocusBrandBlock,
  verbatim: VerbatimBlock,
  'segment-profile': SegmentProfileBlock,
  'act-divider': ActDividerBlock,
  marquee: MarqueeBlock,
  cover: CoverBlock,
  'question-cards': QuestionCardsBlock,
  'exec-summary': ExecSummaryBlock,
  'insight-cards': InsightCardsBlock,
  'opp-table': OppTableBlock,
  'ranked-bars': RankedBarsBlock,
  'stat-callout': StatCalloutBlock,
  'compare-cards': CompareCardsBlock,
  'closing-frame': ClosingFrameBlock,
};

export function resolveBlock(type: string) {
  return REGISTRY[type] ?? GenericBlock;
}
